import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  Box, 
  Typography,
  Button,
  Paper,
  Chip,
  Grid,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import {
  Bolt as BoltIcon,
  Phone as PhoneIcon,
  Chat as ChatIcon,
  AttachMoney as MoneyIcon,
  Timer,
  CreditScore,
  Token,
  Message
  
} from '@mui/icons-material';
import { getWebhookData } from '../../../redux/actions/billingActions';
import useCallOnce from '../../../hooks/useCallOnce';

function BillingUsagePlans() {
  const dispatch = useDispatch();
  const { user, userpackageinfos } = useSelector((state) => state.auth);
  const { webhookData, loading, error } = useSelector((state) => state.billing);
  
  // Fetch webhook data using useCallOnce - only once
  useCallOnce('billing/getWebhookData', () => {
    dispatch(getWebhookData());
  });
  
  // Calculate trial dates based on user creation date
  const getTrialDates = () => {
    if (!userpackageinfos?.createdAt) return { startDate: 'N/A', endDate: 'N/A' };
    
    const startDate = new Date(userpackageinfos.createdAt);
    const endDate = new Date(startDate.getTime() + (15 * 24 * 60 * 60 * 1000)); // 15 days
    
    const formatDate = (date) => {
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    };
    
    return {
      startDate: formatDate(startDate),
      endDate: formatDate(endDate)
    };
  };
  
  // Calculate webhook data summary
  const getWebhookSummary = () => {
    if (!webhookData || !webhookData.data || !Array.isArray(webhookData.data)) return null;
    
    const webhookArray = webhookData.data;
    
    const summary = {
      totalWebhooks: webhookArray.length,
      totalCost: 0,
      paidWebhooks: 0,
      freeWebhooks: 0,
      totalDuration: 0,
      averageDuration: 0,
      totalLLMCharge: 0,
      totalCallCharge: 0,
      textOnlyCount: 0,
      voiceCount: 0,
      successfulCalls: 0,
      failedCalls: 0,
      percredit: webhookData?.getCosting[0]?.percredit || 0,
      agentZenaPercentage: webhookData?.getCosting[0]?.agentzenapercent || 0
    };
    
    webhookArray.forEach(webhook => {
      // Cost calculation
      if (webhook.price) {
        summary.totalCost += webhook.price;
        summary.paidWebhooks++;
      } else {
        summary.freeWebhooks++;
      }
      
      // Duration calculation
      if (webhook.body?.data?.metadata?.call_duration_secs) {
        summary.totalDuration += webhook.body.data.metadata.call_duration_secs;
      }
      
      // Charging details
      if (webhook.body?.data?.metadata?.charging) {
        const charging = webhook.body.data.metadata.charging;
        if (charging.llm_charge) summary.totalLLMCharge += charging.llm_charge;
        if (charging.call_charge) summary.totalCallCharge += charging.call_charge;
      }
      
      // Text vs Voice
      if (webhook.body?.data?.metadata?.text_only) {
        summary.textOnlyCount++;
      } else {
        summary.voiceCount++;
      }
      
      // Call success/failure
      if (webhook.body?.data?.analysis?.call_successful === 'success') {
        summary.successfulCalls++;
      } else {
        summary.failedCalls++;
      }
    });
    
    summary.averageDuration = summary.totalWebhooks > 0 ? 
      Math.round(summary.totalDuration / summary.totalWebhooks) : 0;
    
    return summary;
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0 min';
    const minutes = Math.round(seconds / 60);
    return `${minutes} min`;
  };
  
  const { startDate, endDate } = getTrialDates();
  const webhookSummary = getWebhookSummary();

  const totalCreditsCost = webhookSummary?.totalCost * webhookSummary?.percredit;
  const totalZenaCost = (webhookSummary?.agentZenaPercentage / 100) * totalCreditsCost;
  const totalAmount = totalCreditsCost + totalZenaCost;
  
  return (
    <Box sx={{ width: '100%', px: { xs: 0, sm: 0 } }}>
      {/* Usage & Plans Content */}
      <Box sx={{ width: '100%' }}>
        {/* Trial Information & Billing Frequency */}
        {
          userpackageinfos && (
            <Paper sx={{ 
          p: { xs: 2, sm: 3 }, 
          mb: 3,
          border: '1px solid #e5e7eb',
          borderRadius: 2,
          boxShadow: 'none',
          backgroundColor: '#ffffff'
        }}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 2, sm: 0 }
          }}>
            <Typography variant="body1" sx={{ 
              color: '#111827',
              fontSize: { xs: '14px', sm: '15px' },
              fontWeight: 500
            }}>
              Your free trial started on <strong>{startDate}</strong> and will expire on <strong>{endDate}</strong>
            </Typography>
            
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 2,
              flexDirection: { xs: 'column', sm: 'row' }
            }}>
              {/* <Typography variant="body2" sx={{ 
                color: '#6b7280',
                fontSize: { xs: '13px', sm: '14px' }
              }}>
                Billed monthly
              </Typography> */}
              <Button
                variant="outlined"
                onClick={() => window.open('https://admin.shopify.com/store/dev-dl1961/apps/agentzena/app/pricing', '_blank', 'noopener')}
                size="small"
                sx={{
                  borderColor: '#111827',
                  color: '#111827',
                  fontWeight: 500,
                  textTransform: 'none',
                  fontSize: { xs: '11px', sm: '12px' },
                  px: { xs: 1.5, sm: 2 },
                  py: { xs: 0.5, sm: 0.75 },
                  borderRadius: 1.5,
                  minWidth: 'auto',
                  '&:hover': {
                    borderColor: '#374151',
                    backgroundColor: '#f9fafb',
                    color: '#374151'
                  }
                }}
              >
                Update
              </Button>
            </Box>
          </Box>
            </Paper>
        )}

        {/* Loading State */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={40} sx={{ color: '#111827' }} />
          </Box>
        )}

        {/* Error State */}
        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 3,
              '& .MuiAlert-message': { color: '#dc2626' },
              '& .MuiAlert-icon': { color: '#dc2626' }
            }}
          >
            {error}
          </Alert>
        )}

        {/* Webhook Data Summary */}
        {webhookSummary && webhookData && !loading && (
          <>
            <Grid container spacing={3} sx={{ mb: 3 }}>
               
              
              <Grid size={{ xs: 12, md: 4 }}>
                <Paper sx={{ 
                  p: 3,
                  border: '1px solid #e5e7eb',
                  borderRadius: 2,
                  boxShadow: 'none',
                  backgroundColor: '#ffffff',
                  textAlign: 'center'
                }}>
                  <Message sx={{ fontSize: 40, color: '#f59e0b', mb: 1 }} />
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#111827', mb: 1 }}>
                    {(webhookData?.processedData?.results[0]?.total_messages || 0).toLocaleString()}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#6b7280' }}>
                    Messages
                  </Typography>
                </Paper>
              </Grid>
              
              <Grid size={{ xs: 12, md: 4 }}>
                <Paper sx={{ 
                  p: 3,
                  border: '1px solid #e5e7eb',
                  borderRadius: 2,
                  boxShadow: 'none',
                  backgroundColor: '#ffffff',
                  textAlign: 'center'
                }}>
                  <Timer sx={{ fontSize: 40, color: '#8b5cf6', mb: 1 }} />
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#111827', mb: 1 }}>
                    {formatDuration(webhookData?.processedData?.results[0]?.total_call_duration_secs)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#6b7280' }}>
                    Minutes
                  </Typography>
                </Paper>
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <Paper sx={{ 
                  p: 3,
                  border: '1px solid #e5e7eb',
                  borderRadius: 2,
                  boxShadow: 'none',
                  backgroundColor: '#ffffff',
                  textAlign: 'center'
                }}>
                  <PhoneIcon sx={{ fontSize: 40, color: '#10b981', mb: 1 }} />
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#111827', mb: 1 }}>
                    {(webhookData?.processedData?.results[0]?.total_conversation || 0).toLocaleString()}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#6b7280' }}>
                    Calls
                  </Typography>
                </Paper>
              </Grid>  
            </Grid>

            {/* <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid size={{ xs: 12, md: 4 }}>
                <Paper sx={{ 
                  p: 3,
                  border: '1px solid #e5e7eb',
                  borderRadius: 2,
                  boxShadow: 'none',
                  backgroundColor: '#ffffff',
                  textAlign: 'center'
                }}>
                  <ChatIcon sx={{ fontSize: 40, color: '#14B789', mb: 1 }} />
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#111827', mb: 1 }}>
                    {(webhookData?.processedData?.results[0]?.total_call_credits || 0).toLocaleString()}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#6b7280' }}>
                    Chat Credits
                  </Typography>
                </Paper>
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <Paper sx={{ 
                  p: 3,
                  border: '1px solid #e5e7eb',
                  borderRadius: 2,
                  boxShadow: 'none',
                  backgroundColor: '#ffffff',
                  textAlign: 'center'
                }}>
                  <Token sx={{ fontSize: 40, color: '#555', mb: 1 }} />
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#111827', mb: 1 }}>
                    {(webhookData?.processedData?.results[0]?.lmmCredits || 0).toLocaleString()}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#6b7280' }}>
                    LLM Credit
                  </Typography>
                </Paper>
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <Paper sx={{ 
                  p: 3,
                  border: '1px solid #e5e7eb',
                  borderRadius: 2,
                  boxShadow: 'none',
                  backgroundColor: '#ffffff',
                  textAlign: 'center'
                }}>
                  <Token sx={{ fontSize: 40, color: '#555', mb: 1 }} />
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#111827', mb: 1 }}>
                    {((webhookData?.processedData?.results[0]?.lmmCredits || 0) + (webhookData?.processedData?.results[0]?.total_call_credits || 0)).toLocaleString()}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#6b7280' }}>
                    Total Credits
                  </Typography>
                </Paper>
              </Grid>
            </Grid>

            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid size={{ xs: 12, md: 4 }}>
                <Paper sx={{ 
                  p: 3,
                  border: '1px solid #e5e7eb',
                  borderRadius: 2,
                  boxShadow: 'none',
                  backgroundColor: '#ffffff',
                  textAlign: 'center'
                }}>
                  <MoneyIcon sx={{ fontSize: 40, color: '#ef4444', mb: 1 }} />
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#111827', mb: 1 }}>
                    {`$${((webhookData?.getCosting?.[0]?.percredit * webhookData?.processedData?.results[0]?.total_call_credits) || 0).toFixed(2)}`}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#6b7280' }}>
                    Chat Cost
                  </Typography>
                </Paper>
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <Paper sx={{ 
                  p: 3,
                  border: '1px solid #e5e7eb',
                  borderRadius: 2,
                  boxShadow: 'none',
                  backgroundColor: '#ffffff',
                  textAlign: 'center'
                }}>
                  <MoneyIcon sx={{ fontSize: 40, color: '#ef4444', mb: 1 }} />
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#111827', mb: 1 }}>
                    {`$${(webhookData?.processedData?.totalCharges_llm || 0).toFixed(2)}`}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#6b7280' }}>
                    LLM Cost
                  </Typography>
                </Paper>
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <Paper sx={{ 
                  p: 3,
                  border: '1px solid #e5e7eb',
                  borderRadius: 2,
                  boxShadow: 'none',
                  backgroundColor: '#ffffff',
                  textAlign: 'center'
                }}>
                  <MoneyIcon sx={{ fontSize: 40, color: '#ef4444', mb: 1 }} />
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#111827', mb: 1 }}>
                    {`$${((webhookData?.processedData?.totalCharges_llm + (webhookData?.getCosting?.[0]?.percredit * webhookData?.processedData?.results[0]?.total_call_credits)) || 0).toFixed(2)}`}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#6b7280' }}>
                    Total Cost
                  </Typography>
                </Paper>
              </Grid>
            </Grid> */}

            {/* <Paper sx={{ 
              p: { xs: 2, sm: 3 },
              mb: 3,
              border: '1px solid #e5e7eb',
              borderRadius: 2,
              boxShadow: 'none',
              backgroundColor: '#ffffff'
            }}>
             
              
              <Grid container spacing={5}>
                <Grid size={{ xs: 12, md: 4 }}>
                <Typography variant="h6" sx={{ 
                  fontWeight: 600,
                  color: '#111827',
                  fontSize: { xs: '1.1rem', sm: '1.25rem' },
                  mb: 3
                }}>
                  Usage Summary
                </Typography>
                  <Box sx={{ mb: 2 }}>
                    
                   
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" sx={{ color: '#6b7280' }}>LLM Credits:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{webhookSummary.totalLLMCharge.toLocaleString()}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" sx={{ color: '#6b7280' }}>Call Credits:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{webhookSummary.totalCallCharge.toLocaleString()}</Typography>
                    </Box>

                                         <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, borderTop: '1px solid #e5e7eb', pt: 2 }}>
                       <Typography variant="body2" sx={{ fontWeight: 600, color: '#111827' }}>Total Credits:</Typography>
                       <Typography variant="body2" sx={{ fontWeight: 600, color: '#111827' }}>{webhookSummary.totalCost.toLocaleString()}</Typography>
                     </Box>

                    
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                <Typography variant="h6" sx={{ 
                  fontWeight: 600,
                  color: '#111827',
                  fontSize: { xs: '1.1rem', sm: '1.25rem' },
                  mb: 3
                }}>
                  Cost Summary
                </Typography>
                  <Box sx={{ mb: 2 }}>
                    
                  
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" sx={{ color: '#6b7280' }}>Per Credit Cost ({Math.round(webhookSummary.totalDuration / 60)} min):</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>${webhookSummary.percredit || 0.00}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" sx={{ color: '#6b7280' }}>Total Credits Cost:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>${totalCreditsCost.toFixed(4)}</Typography>
                    </Box>
                   
                    

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, borderTop: '1px solid #e5e7eb', pt: 2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#111827' }}>Total Cost:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#111827' }}>
                      ${totalAmount.toFixed(4)}
                      </Typography>
                    </Box>

                    
                  </Box>
                </Grid>
                
                
              </Grid>
            </Paper> */}

            {/* Webhook Data Table */}
            {/* {webhookData && webhookData.data && webhookData.data.length > 0 && (
              <Paper sx={{ 
                p: { xs: 2, sm: 3 },
                mb: 3,
                border: '1px solid #e5e7eb',
                borderRadius: 2,
                boxShadow: 'none',
                backgroundColor: '#ffffff'
              }}>
                <Typography variant="h6" sx={{ 
                  fontWeight: 600,
                  color: '#111827',
                  fontSize: { xs: '1.1rem', sm: '1.25rem' },
                  mb: 3
                }}>
                  Recent Webhook Events
                </Typography>
                
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Duration</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Cost</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Mode</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {webhookData.data.map((webhook, index) => (
                        <TableRow key={webhook._id || index}>
                          <TableCell>
                            {new Date(webhook.created_at).toLocaleDateString()}
                          </TableCell>
                         
                          <TableCell>
                            {webhook.body?.data?.metadata?.call_duration_secs || 0}s
                          </TableCell>
                          <TableCell>
                            {webhook.price ? `$${webhook.price}` : '-'}
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={webhook.body?.data?.analysis?.call_successful || 'Unknown'} 
                              size="small"
                              sx={{
                                backgroundColor: webhook.body?.data?.analysis?.call_successful === 'success' ? '#10b981' : '#ef4444',
                                color: '#ffffff',
                                fontWeight: 500
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={webhook.body?.data?.metadata?.text_only ? 'Text' : 'Voice'} 
                              size="small"
                              sx={{
                                backgroundColor: webhook.body?.data?.metadata?.text_only ? '#3b82f6' : '#8b5cf6',
                                color: '#ffffff',
                                fontWeight: 500
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                
              </Paper>
            )} */}
          </>
        )}
      </Box>
    </Box>
  );
}

export default BillingUsagePlans;
